import type { Item } from '@mas-flow/types';

// Maps a column id to a cell value extracted from an Item. Add columns here
// as needed; views can reference column ids by name.
const CELL_EXTRACTORS: Record<string, (it: Item) => string | number | null> = {
  rank: () => '',                        // rank is render-only; export-time omitted unless caller injects it
  title: (it) => it.title,
  category: (it) => it.categoryId,
  bv: (it) => it.scores.businessValue,
  tc: (it) => it.scores.timeCriticality,
  rr: (it) => it.scores.riskOpportunity,
  size: (it) => it.scores.jobSize,
  cod: (it) => it.costOfDelay,
  wsjf: (it) => it.wsjf,
  priorityBand: (it) => it.priorityBand,
  confidence: (it) => it.confidence,
  decision: (it) => it.decision,
  lifecycle: (it) => it.lifecycle,
  owner: (it) => it.owner?.displayName ?? '',
  updated: (it) => it.updatedAt,
  anchorWhy: (it) => it.anchorWhy ?? '',
  voters: (it) => Object.keys(it.votes ?? {}).length,
};

function cellValue(it: Item, col: string): string | number | null {
  const fn = CELL_EXTRACTORS[col];
  return fn ? fn(it) : '';
}

function escapeCsv(v: string | number | null): string {
  if (v == null) return '';
  const s = String(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

export function toCsv(items: Item[], columns: readonly string[]): string {
  const header = columns.join(',');
  const rows = items.map((it) => columns.map((c) => escapeCsv(cellValue(it, c))).join(','));
  return [header, ...rows].join('\n');
}

export async function toXlsxBuffer(
  items: Item[],
  columns: readonly string[],
  sheetName = 'Portfolio',
): Promise<ArrayBuffer> {
  const { Workbook } = await import('exceljs');
  const wb = new Workbook();
  const ws = wb.addWorksheet(sheetName);
  ws.addRow(columns as string[]);
  for (const it of items) {
    ws.addRow(columns.map((c) => cellValue(it, c)));
  }
  const buf = await wb.xlsx.writeBuffer();
  return buf as ArrayBuffer;
}
